import { useEffect, useMemo } from 'react'
import { useLocation } from 'wouter'
import {
    Alert,
    Box,
    Button,
    Chip,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import DashboardLayout from '../components/DashboardLayout'

// Lands here when the IdP redirects back from a Test-login round-
// trip (the button on the Edit page or the Onboard success card).
//
// In a real product login (mp-v2), this is where the one-time
// `?code=` is exchanged for a token pair. In staff-console, we
// don't actually want to log the staff member in *as* the federated
// user — they're just verifying the SAML chain works. So we render
// a confirmation panel showing the code that came back and let
// staff close the tab.
//
// The chain we just exercised:
//   staff-console Test login button
//     -> hl-accounts /sso/saml/<slug>/login
//        -> AuthnRequest to IdP, browser auths there
//        -> POST to hl-accounts /sso/saml/acs (validates assertion,
//           JIT-provisions, mints one-time code)
//        -> 302 redirect to <staff-console>/sso/callback?code=…
//        -> THIS PAGE.
//
// If we got here with `?code=`, every step in that chain succeeded.
// If we got here with `?error=`, hl-accounts surfaced an error and
// staff need to fix the connection. The page renders accordingly.

/**
 * Known `?error=` codes we render with friendly copy. Anything not
 * in this map renders an opaque "rejected" message — the URL params
 * are user-controllable (a phisher could craft a link like
 * `/sso/callback?error=Your+password+is+expired&error_description=Click+here…`
 * and present it as a real callback), so passing arbitrary strings
 * straight into the DOM is a phishing surface even though React's
 * text-children escaping rules out classical XSS.
 *
 * When hl-accounts grows new error codes, add them here. The
 * unknown-code branch deliberately doesn't surface the raw value so
 * staff aren't trained to trust whatever the URL says.
 */
const KNOWN_ERROR_COPY: Record<string, string> = {
    access_denied: 'The IdP denied authentication for this user.',
    assertion_rejected: 'The SAML assertion was rejected by hl-accounts.',
    invalid_assertion: 'The SAML assertion failed validation.',
    invalid_request: 'The callback request was malformed.',
    relay_state_invalid: 'The RelayState token failed integrity check.',
    server_error: 'hl-accounts hit an internal error processing the callback.',
    temporarily_unavailable: 'hl-accounts is temporarily unavailable.',
    unauthorized_client: 'The connection is not authorised for this flow.',
}

export default function SsoCallbackPage() {
    const [, setLocation] = useLocation()

    // Read query-string params off `window.location` once on mount.
    // Wouter's useLocation doesn't carry them; we don't need them
    // to reactively re-render anyway.
    //
    // We intentionally don't capture `error_description`: it's
    // attacker-controllable on a hand-crafted callback URL and
    // we never render arbitrary text from it. The mount-effect
    // below still strips it from the URL so a refresh leaves a
    // clean address bar.
    const params = useMemo(() => {
        const search = new URLSearchParams(window.location.search)
        return {
            code: search.get('code'),
            error: search.get('error'),
        }
    }, [])

    // Defensively strip the `?code=` from the URL so a refresh
    // doesn't leave a stale code in the address bar / browser
    // history. Cosmetic — the code is single-use on hl-accounts and
    // can't be replayed even if it leaked.
    useEffect(() => {
        if (!params.code && !params.error) return
        const url = new URL(window.location.href)
        url.searchParams.delete('code')
        url.searchParams.delete('error')
        url.searchParams.delete('error_description')
        window.history.replaceState(
            window.history.state,
            '',
            url.pathname + (url.search || '') + url.hash
        )
    }, [])

    // An `error` param always means the round-trip failed — treat it as the
    // failure case even if a `code` is also present (e.g. a hand-crafted URL
    // carrying both), so we never render the success card over a real error.
    const isError = !!params.error
    const isOk = !!params.code && !params.error
    const isUnknown = !params.code && !params.error

    // Resolve the error code to friendly copy. Unknown codes get an
    // opaque "rejected" message — never surface the raw `error` or
    // `error_description` URL params: they're attacker-controllable
    // and would otherwise let a phisher craft a "your password is
    // expired" message that renders as if it came from us.
    const errorCopy =
        params.error && KNOWN_ERROR_COPY[params.error]
            ? KNOWN_ERROR_COPY[params.error]
            : 'The IdP redirected back with an error. Check hl-accounts logs for the specific reason — the URL parameter is not displayed because it could be attacker-supplied on a hand-crafted link.'

    return (
        <DashboardLayout>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 4,
                    paddingBottom: 8,
                }}
            >
                <Paper sx={{ p: 4, width: '100%', maxWidth: 720 }}>
                    {isOk && (
                        <>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                sx={{ mb: 1 }}
                            >
                                <CheckCircleIcon color="success" />
                                <Typography variant="h6">
                                    SAML round-trip successful
                                </Typography>
                            </Stack>
                            <Typography
                                variant="body2"
                                sx={{ color: 'gray', mb: 2 }}
                            >
                                The IdP authenticated, hl-accounts validated the
                                assertion, JIT-provisioned the user, and
                                redirected back here with a one-time exchange
                                code. The connection is wired correctly end-
                                to-end.
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                rowGap={1}
                                sx={{ mb: 3 }}
                            >
                                <Chip
                                    color="success"
                                    label="IdP accepted credentials"
                                />
                                <Chip
                                    color="success"
                                    label="Assertion signature OK"
                                />
                                <Chip
                                    color="success"
                                    label="JIT provisioning OK"
                                />
                                <Chip
                                    color="success"
                                    label={`Code issued: ${params.code!.slice(
                                        0,
                                        12
                                    )}…`}
                                />
                            </Stack>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                The code above isn&apos;t exchanged here — that
                                only happens in mp-v2&apos;s real callback.
                                Staff- console only verifies that hl-accounts
                                issued a code, which proves every step before it
                                worked. Safe to close this tab.
                            </Alert>
                        </>
                    )}

                    {isError && (
                        <>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                sx={{ mb: 1 }}
                            >
                                <ErrorOutlineIcon color="error" />
                                <Typography variant="h6">
                                    SAML round-trip rejected
                                </Typography>
                            </Stack>
                            <Typography
                                variant="body2"
                                sx={{ color: 'gray', mb: 2 }}
                            >
                                The IdP redirected back with an error rather
                                than a one-time code. The connection config
                                likely needs work — check hl-accounts&apos;s
                                logs (`sso assertion rejected` is the catch-all
                                message; specifics go to{' '}
                                <code>console.warn</code>).
                            </Typography>
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {errorCopy}
                            </Alert>
                        </>
                    )}

                    {isUnknown && (
                        <>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                sx={{ mb: 1 }}
                            >
                                <ErrorOutlineIcon color="warning" />
                                <Typography variant="h6">
                                    Unexpected callback
                                </Typography>
                            </Stack>
                            <Typography
                                variant="body2"
                                sx={{ color: 'gray', mb: 2 }}
                            >
                                This page expects to land here from a Test-
                                login round-trip carrying a <code>?code=</code>{' '}
                                or <code>?error=</code> query parameter. Neither
                                was present, so either the URL was hand-typed or
                                hl-accounts redirected back with an unexpected
                                shape.
                            </Typography>
                        </>
                    )}

                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<FormatListBulletedIcon />}
                            onClick={() =>
                                setLocation('/manage-sso-connections')
                            }
                        >
                            Back to connections list
                        </Button>
                    </Stack>
                </Paper>
            </Box>
        </DashboardLayout>
    )
}
